import { keepPreviousData, useQuery } from '@tanstack/vue-query'
import { refDebounced } from '@vueuse/core'
import { computed, ref, toValue } from 'vue'

import type { SearchableQueryConfig, UseSearchableQueryOptions } from '@/support/query/useSearchableQuery.types'

import { DEFAULT_SEARCH_DEBOUNCE, STALE_TIMES } from '@/support/query/constants'

/**
 * The shared body of every `use[Entity]Query` that searches a domain by a typed term.
 *
 * Owns the term, its debounce, the cache key and the idle rule. The domain composable supplies the
 * request and the cache root, so a fix to the search behaviour lands in one place.
 */
export const useSearchableQuery = <TItem, TParams extends object = object>(
  options: UseSearchableQueryOptions<TParams>,
  config: SearchableQueryConfig<TItem, TParams>
) => {
  const searchQuery = ref(toValue(options.searchQuery) ?? '')
  const debouncedSearch = refDebounced(searchQuery, options.debounce ?? DEFAULT_SEARCH_DEBOUNCE)

  const query = useQuery({
    queryKey: computed(() => [...config.queryKey, 'search', debouncedSearch.value, toValue(options.params) ?? {}]),
    queryFn: () => config.fetchItems({ search: debouncedSearch.value, params: toValue(options.params) }),
    enabled: computed(
      () => (toValue(options.enabled) ?? true) && debouncedSearch.value.length >= (config.minSearchLength ?? 0)
    ),
    placeholderData: keepPreviousData,
    staleTime: config.staleTime ?? STALE_TIMES.DEFAULT
  })

  return {
    ...query,
    searchQuery
  }
}
